/**
 * A setup composed in conversation, and the two functions that stand between
 * it and the database. Phase 1 of docs/plans/ai-setup-and-agent.md.
 *
 * Decision 1 of that plan: a composed setup is a `SetupSpec` plus a word
 * table. The spec speaks keys, as every spec does, and `words` gives each key
 * the person's own word. `validateSpec(spec, {...en, ...words})` then
 * checks it with the same validator the five shipped templates pass through,
 * and `applySpec` writes it with the same code. No business word enters
 * the schema: the person's words live only in `words`, and reach a row only
 * where a shipped template's word would have - `app.record.name`,
 * `app.field.label`, an option's `label`.
 *
 * Two functions, kept apart on purpose.
 *
 *   readComposedSetup   shape and limits, by hand, in the style of the
 *                       server's `onboarding-input.ts`. It refuses on the
 *                       first fault, because a body that is not the shape is
 *                       not a setup to list problems about.
 *   composeProblems     every problem with a setup that IS the shape, at once,
 *                       so the agent fixes one round trip and not one mistake
 *                       per round trip. Pure, never throws.
 */
import { isCurrencyCode } from './currencies';
import { enFlat, nounShipped } from './i18n/en';
import type { SetupUnderstanding } from './setup-understanding';
import { validateSpec, type Catalogue, type SpecProblem } from './spec/validate-spec';
import type { SpecField, SpecRecord, SetupSpec } from './spec/spec';

/* ------------------------------------------------------------------ the shape */

/** The id every composed spec carries. Nothing is stamped on a record from it. */
export const COMPOSED_SPEC_ID = 'composed';

/** Key to the person's own word. Every key here must be absent from `en`. */
export type ComposedWords = Readonly<Record<string, string>>;

export type ComposedSetup = {
  businessName: string;
  /** ISO 4217. `isCurrencyCode` decides. */
  currency: string;
  /** id `composed`, version 1, every `*Key` a key into `words` or into `en`. */
  spec: SetupSpec;
  words: ComposedWords;
  /**
   * What the build could not hold with the parts it has (Decision 18 of
   * docs/plans/entry-lines-and-layout.md), carried so the route writes each
   * to app.unmet_need when the person creates the business. Never shown to
   * the person. Absent on a setup that noted none.
   */
  unmetNeeds?: readonly ComposedUnmetNeed[];
};

/**
 * The five levels a part is chosen at (Section 7.5 of
 * docs/records-platform-architecture.md), in order: level 1 is `data`, level
 * 5 is `view`. The one list the setup wire, the compose reader and the server
 * table check against.
 */
export const UNMET_NEED_LEVELS = ['data', 'surface', 'arrangement', 'lifecycle', 'view'] as const;
export type UnmetNeedLevel = (typeof UNMET_NEED_LEVELS)[number];

/** One need the parts could not hold: the level, the kind of business, general words. */
export type ComposedUnmetNeed = {
  readonly level: UnmetNeedLevel;
  readonly archetype: string;
  readonly need: string;
};

/**
 * POST /v1/onboarding/compose body. `clientKey` and `setup` make the
 * business; the two optional members are the business's first memory
 * (docs/plans/agent-business-memory.md, decisions 1, 3 and 13).
 *
 *   understanding   what the interview noted, written as app.business_understanding
 *                   and seeded as the first `business` facts, one per line
 *   transcript      the gather turns, the AI SDK's `UIMessage[]`, stored as a
 *                   thread under the new business with `screen: 'setup'`. Typed
 *                   as `unknown[]` here because this package does not depend
 *                   on `ai`; the route validates it the way chat does
 *   source          `call` when the transcript is the live call's note-taker
 *                   transcript; absent or `interview` otherwise
 *
 * A phone that sends neither still makes a business. The `created: false`
 * replay of a `clientKey` writes none of it a second time.
 */
export type ComposeRequest = {
  readonly clientKey: string;
  readonly setup: ComposedSetup;
  readonly understanding?: SetupUnderstanding;
  readonly transcript?: readonly unknown[];
  readonly source?: 'interview' | 'call';
  /**
   * The thread the client kept the setup's interview under: the transcript
   * is stored as the new business's first thread under this id, so live mode
   * carries on into it after Create (step 5.2). Absent mints one.
   */
  readonly threadId?: string;
  /**
   * What OS learned from the setup files (docs/plans/setup-from-files.md,
   * Decided 8): each row becomes a business fact, with the file it came from,
   * once that file is the new workspace's.
   */
  readonly learned?: readonly { readonly about: string; readonly text: string; readonly fileId: string }[];
  /**
   * The setup files the page still holds (Decided 4): only these become the
   * new workspace's. A file the person removed, or one a reload lost, stays
   * theirs alone. Absent, none is adopted.
   */
  readonly fileIds?: readonly string[];
};

/**
 * The ceilings. Generous for a business anyone would describe in a
 * conversation, and small enough that a runaway model cannot make a body the
 * validator takes a second to walk.
 */
export const COMPOSE_LIMITS = {
  businessName: 120,
  /** Seed rows' text values are words too: the 9.8 board's store with its lists' rows beside a setup at every ceiling (compose-limits.test.ts). */
  words: 8000,
  word: 80,
  records: 40,
  fields: 40,
  unmetNeeds: 20,
  unmetArchetype: 60,
  unmetNeed: 280,
  /** The rows one list starts with (decision 10); a longer list comes in as an import job after Build (step 8.3). */
  seedRows: 2000,
} as const;

/**
 * The most bytes POST /v1/onboarding/compose reads. A setup at every ceiling
 * (`COMPOSE_LIMITS.records` records of `.fields` fields) measures well under
 * it (compose-limits.test.ts); Fastify's 1 MiB default is too tight for one.
 */
export const COMPOSE_BODY_LIMIT = 4 * 1024 * 1024;

/** A word key: short, lower case, addressable. Dots so keys can be grouped. */
export const WORD_KEY = /^[a-z0-9_.-]{1,80}$/;


const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/* ---------------------------------------------------------------- the reader */

export type ComposeRefusal = { code: string; message: string };

export type ComposedSetupResult =
  | { ok: true; value: ComposedSetup }
  | { ok: false; refusal: ComposeRefusal };

const no = (code: string, message: string): ComposedSetupResult => ({
  ok: false,
  refusal: { code, message },
});

/**
 * The unmet needs of a setup, read without trusting them. A note is never
 * worth the create, so one that is not the shape is dropped rather than
 * refused, and past the ceiling the rest are dropped too. The server judges
 * each once more (a phone number, an email) before it writes it.
 */
export const readUnmetNeeds = (raw: unknown): ComposedUnmetNeed[] => {
  if (!Array.isArray(raw)) return [];
  const out: ComposedUnmetNeed[] = [];
  for (const one of raw) {
    if (out.length === COMPOSE_LIMITS.unmetNeeds) break;
    if (!isObject(one)) continue;
    const { level, archetype, need } = one;
    if (typeof level !== 'string' || !(UNMET_NEED_LEVELS as readonly string[]).includes(level)) continue;
    if (typeof archetype !== 'string' || typeof need !== 'string') continue;
    const kind = archetype.trim();
    const words = need.trim();
    if (kind === '' || words === '' || kind.length > COMPOSE_LIMITS.unmetArchetype || words.length > COMPOSE_LIMITS.unmetNeed) continue;
    out.push({ level: level as UnmetNeedLevel, archetype: kind, need: words });
  }
  return out;
};

/**
 * Read a composed setup off the wire.
 *
 * Shape and ceilings only. Whether the spec is sound is `composeProblems`'
 * question, asked next, and it lists every answer at once; this reader stops
 * at the first fault because there is nothing to list about a body that is
 * not the shape. The spec itself is not walked here beyond its size: the
 * validator already reads every piece of it without trusting any.
 */
export const readComposedSetup = (raw: unknown): ComposedSetupResult => {
  if (!isObject(raw)) return no('SETUP_NOT_OBJECT', 'setup must be an object.');

  const businessName = typeof raw.businessName === 'string' ? raw.businessName.trim() : '';
  if (businessName.length === 0 || businessName.length > COMPOSE_LIMITS.businessName) {
    return no(
      'BUSINESS_NAME_INVALID',
      `businessName must be 1 to ${COMPOSE_LIMITS.businessName} characters.`,
    );
  }

  // Upper-cased here so `isCurrencyCode`, which forgives case, and the column,
  // which does not, agree on what was sent.
  const currency = typeof raw.currency === 'string' ? raw.currency.trim().toUpperCase() : '';
  if (currency === '') return no('CURRENCY_MISSING', 'currency must be a three-letter ISO 4217 code.');

  if (!isObject(raw.words)) {
    return no('WORDS_INVALID', 'words must be an object keyed by the keys the spec carries.');
  }
  const entries = Object.entries(raw.words);
  if (entries.length > COMPOSE_LIMITS.words) {
    return no('WORDS_TOO_MANY', `words holds ${entries.length} entries; the ceiling is ${COMPOSE_LIMITS.words}.`);
  }
  const words: Record<string, string> = {};
  for (const [key, value] of entries) {
    if (!WORD_KEY.test(key)) {
      return no('WORD_KEY_INVALID', `words key "${key}" is not lower case letters, digits, dots, dashes and underscores.`);
    }
    const word = typeof value === 'string' ? value.trim() : '';
    if (word.length === 0 || word.length > COMPOSE_LIMITS.word) {
      return no('WORD_INVALID', `words["${key}"] must be 1 to ${COMPOSE_LIMITS.word} characters.`);
    }
    words[key] = word;
  }

  if (!isObject(raw.spec)) return no('SPEC_INVALID', 'spec must be a template spec object.');
  const records = Array.isArray(raw.spec.records) ? raw.spec.records : [];
  if (records.length > COMPOSE_LIMITS.records) {
    return no('RECORDS_TOO_MANY', `spec declares ${records.length} records; the ceiling is ${COMPOSE_LIMITS.records}.`);
  }
  for (const record of records) {
    const fields = isObject(record) && Array.isArray(record.fields) ? record.fields : [];
    if (fields.length > COMPOSE_LIMITS.fields) {
      return no(
        'FIELDS_TOO_MANY',
        `record "${String(isObject(record) ? record.key : '?')}" declares ${fields.length} fields; the ceiling is ${COMPOSE_LIMITS.fields}.`,
      );
    }
    const seedRows = isObject(record) && Array.isArray(record.seedRows) ? record.seedRows.length : 0;
    if (seedRows > COMPOSE_LIMITS.seedRows) {
      return no(
        'SEED_ROWS_TOO_MANY',
        `list "${String(isObject(record) ? record.key : '?')}" seeds ${seedRows} rows; the ceiling is ${COMPOSE_LIMITS.seedRows}.`,
      );
    }
  }

  const unmetNeeds = readUnmetNeeds(raw.unmetNeeds);

  return {
    ok: true,
    value: { businessName, currency, spec: raw.spec as unknown as SetupSpec, words, ...(unmetNeeds.length === 0 ? {} : { unmetNeeds }) },
  };
};

/* ------------------------------------------------------------- the problems */

/**
 * The catalogue a composed spec resolves against: the shipped words, then the
 * person's. A composed key may not shadow a shipped one, which
 * `composeProblems` refuses, so the order here never decides anything.
 */
export const composedCatalogue = (setup: Pick<ComposedSetup, 'words'>): Catalogue => ({
  ...enFlat,
  ...setup.words,
});

/**
 * Every problem with a composed setup, at once. Empty means it may be applied.
 *
 * The spec goes through the same validator the JSON pack does, against the
 * merged catalogue, with `custom` allowed. What this adds is what a composed
 * spec alone can get wrong: a word that shadows a shipped one, a currency the
 * list does not hold, a tier that pretends a template made the field, and a noun
 * the device could not say.
 */
export const composeProblems = (setup: ComposedSetup): SpecProblem[] => {
  const problems: SpecProblem[] = [];
  const { spec } = setup;
  const at = COMPOSED_SPEC_ID;

  if (!isObject(spec)) {
    return [{ code: 'SPEC_NOT_OBJECT', message: 'The spec is not an object.', path: at }];
  }
  if (spec.id !== COMPOSED_SPEC_ID) {
    problems.push({
      code: 'SPEC_ID_NOT_COMPOSED',
      message: `A composed spec carries the id "${COMPOSED_SPEC_ID}", not "${String(spec.id)}".`,
      path: at,
    });
  }
  if (spec.version !== 1) {
    problems.push({ code: 'SPEC_VERSION_NOT_ONE', message: 'A composed spec is version 1.', path: at });
  }

  if (!isCurrencyCode(setup.currency)) {
    problems.push({
      code: 'CURRENCY_UNKNOWN',
      message: `"${setup.currency}" is not an active ISO 4217 currency.`,
      path: at,
    });
  }

  // The person's word wins in the merged catalogue, so a composed key that a
  // shipped key already owns would silently rename something every screen
  // says. Refused rather than resolved.
  for (const key of Object.keys(setup.words)) {
    if (enFlat[key] !== undefined) {
      problems.push({
        code: 'WORD_SHADOWS_CATALOGUE',
        message: `words key "${key}" is a shipped catalogue key. A composed word takes a key of its own.`,
        path: `${at}.words[${key}]`,
      });
    }
  }

  // The count rule guards the catalogue card a shipped template shows before
  // anyone picks it. A composed setup may ship without preview rows: the
  // person described the business themselves and the apply never reads them.
  const samples = Array.isArray(spec.sampleEntries) ? spec.sampleEntries.length : 0;
  for (const problem of validateSpec(spec, composedCatalogue(setup), { allowCustomTier: true })) {
    if (samples === 0 && problem.code === 'SAMPLE_COUNT') continue;
    problems.push(problem);
  }

  const records = (Array.isArray(spec.records) ? spec.records : []).filter(isObject) as SpecRecord[];
  for (const record of records) {
    for (const field of (Array.isArray(record.fields) ? record.fields : []).filter(isObject) as SpecField[]) {
      // A composed field is the person's own from the moment it exists: the
      // reason `blank.ts` gives, and the same tier it uses. `primary` here
      // would put a template's lock on a field no template made.
      if (field.tier !== 'custom') {
        problems.push({
          code: 'TIER_NOT_CUSTOM',
          message: `Field "${field.key}" has tier "${field.tier}". A composed field is the person's own, so its tier is custom.`,
          path: `${at}.records[${record.key}].fields[${field.key}]`,
        });
      }
    }
  }

  // A stored noun key is a plural STEM: the device says it as
  // `t(key, { count })`, and i18next picks `_one` or `_other` from the count
  // and the language. Both forms have to be shipped or the first record with
  // two entries throws, so the stem is drawn from the shipped nouns until the
  // device reads a composed one.
  for (const key of [spec.nounKey, spec.entryNounKey]) {
    if (typeof key !== 'string') continue;
    if (!nounShipped(key)) {
      problems.push({
        code: 'NOUN_KEY_NOT_SHIPPED',
        message: `Noun "${key}" is not a shipped noun with its one and other forms. The device cannot say it yet; use one of the noun.* keys.`,
        path: at,
      });
    }
  }

  return problems;
};
